import React, { useState, useRef, useEffect } from 'react';
import { Share2, X as XIcon, Mail, Link2, Check, Facebook, MessageCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";

const ShareMenu = ({ title, text, url, variant = 'icon', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || window.location.href;
  const shareTitle = title || 'Collectively Write';
  const shareText = text || 'Check out Collectively Write — a community for writers who own 100% of their work.';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      name: 'X / Twitter',
      icon: () => (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      action: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
      }
    },
    {
      name: 'Facebook',
      icon: Facebook,
      action: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
      }
    },
    {
      name: 'Email',
      icon: Mail,
      action: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
      }
    },
    {
      name: 'Text / SMS',
      icon: MessageCircle,
      action: () => {
        window.open(`sms:?body=${encodeURIComponent(shareText + ' ' + shareUrl)}`);
      }
    },
    {
      name: copied ? 'Copied!' : 'Copy Link',
      icon: copied ? Check : Link2,
      action: handleCopyLink
    }
  ];

  // Use native share on mobile if available
  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: shareTitle, text: shareText, url: shareUrl }).catch(() => {});
    } else {
      setOpen(!open);
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      {variant === 'icon' ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleNativeShare}
          className="text-text-muted hover:text-brand-accent"
          data-testid="share-menu-trigger"
          title="Share"
        >
          <Share2 className="w-5 h-5" />
        </Button>
      ) : (
        <Button
          onClick={handleNativeShare}
          variant="outline"
          className="gap-2 border-brand-primary/20 hover:border-brand-accent text-text-secondary hover:text-brand-accent"
          data-testid="share-menu-trigger"
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      )}

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#E5E2DC] z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-testid="share-menu-dropdown">
          <div className="px-4 py-3 border-b border-[#E5E2DC]/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-text-primary">Share</span>
            <button onClick={() => setOpen(false)} className="text-text-muted hover:text-text-primary">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="py-1">
            {shareOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.name}
                  onClick={() => {
                    option.action();
                    if (option.name !== 'Copied!' && option.name !== 'Copy Link') {
                      setOpen(false);
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-alt hover:text-text-primary transition-colors"
                  data-testid={`share-option-${option.name.toLowerCase().replace(/[\s\/]/g, '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  {option.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
